import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import { useState, useMemo, useRef, useEffect } from 'react'
import globalStyles from "../../index.module.scss"
import { ClipLoader } from 'react-spinners'
import { standardInputClasses } from './inputStyles';

/**
 * A Catalyst-style Listbox select component that replaces Semantic UI Dropdown.
 * Uses Headless UI Listbox for proper form selection semantics.
 */
const Dropdown = ({
  name,
  id = /** @type {string | undefined} */ (undefined),
  label = '',
  options = /** @type {any[]} */ ([]),
  defaultValue = /** @type {any} */ (undefined),
  hideLabel = false,
  placeholder = "Select an option",
  disabled = false,
  loading = false,
  error = '',
  search = false,
  value = /** @type {any} */ (undefined),
  requiredFlag = false,
  onChange = /** @type {any} */ (undefined),
  multiple = false,
  className = '',
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [buttonWidth, setButtonWidth] = useState(null)
  const buttonRef = useRef(null)

  // Track button width
  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth)
    }
  }, [])
  
  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!search || !searchQuery) return options
    return options.filter(option => 
      option.text?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [options, searchQuery, search])

  // Find the currently selected option
  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value)
  }, [options, value])

  // Handle selection change - maintain Semantic UI compatible event shape
  const handleChange = (selectedValue) => {
    // Create event shape compatible with Semantic UI onChange
    const syntheticEvent = { target: { name, value: selectedValue } }
    const data = { name, value: selectedValue }
    
    if (onChange) {
      onChange(syntheticEvent, data)
    }
    setSearchQuery('')
  }

  return (
    <div className={clsx(globalStyles.controls, className)}>
      <Listbox value={value} onChange={handleChange} disabled={disabled || loading}>
        {!hideLabel && (
          <label className="block text-text-secondary text-sm/6 font-medium">
            {label}
            {requiredFlag && <span className={globalStyles.requiredFlag}> * </span>}
          </label>
        )}
        
        <div className={clsx('relative', !hideLabel && 'mt-1')} ref={buttonRef}>
          <ListboxButton
            id={id}
            className={clsx(
              // Base styles - match Catalyst Input padding
              'relative w-full cursor-default rounded-lg bg-surface py-2.5 sm:py-1.5 pl-3 pr-10 text-left text-base sm:text-sm/6',
              // Border - subtle gray
              'border border-border/80',
              // Focus styles
              'focus:outline-none focus:ring-1 focus:ring-border-strong focus:border-border-strong',
              // Hover
              'hover:border-border-strong',
              // Disabled
              (disabled || loading) && 'bg-surface-muted text-foreground-muted cursor-not-allowed',
              // Error state
              error && 'border-red-500 focus:ring-red-500 focus:border-red-500'
            )}
          >
            <span className={clsx(
              'block truncate',
              !selectedOption && 'text-foreground-subtle'
            )}>
              {selectedOption?.text || placeholder}
            </span>
            
            {/* Loading indicator */}
            {loading && (
              <div className="absolute right-8 top-1/2 -translate-y-[45%] z-50 pointer-events-none bg-surface">
                <ClipLoader color="#6366f1" size={18} />
              </div>
            )}
            
            {/* Dropdown arrow */}
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-foreground-subtle"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <ListboxOptions
            anchor="bottom start"
            transition
            className={clsx(
              // Positioning - portal with anchor, at least as wide as button
              'z-1300 w-(--button-width)',
              // Appearance
              'rounded-md bg-surface shadow-lg',
              // Border
              'ring-1 ring-border',
              // Scrolling
              'max-h-60 overflow-auto',
              // Focus
              'focus:outline-none',
              // Animation
              'transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0'
            )}
          >
            {/* Search input */}
            {search && (
              <div className="sticky top-0 z-10 bg-surface p-2 border-b border-border-subtle">
                <input
                  type="text"
                  className={`${standardInputClasses} w-full px-3 py-1.5 text-sm`}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
            
            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-foreground-muted">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <ListboxOption
                    key={option.key || option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={clsx(
                      'group relative cursor-default select-none py-2 pl-3 pr-9 mx-1 rounded-lg text-sm/6',
                      'text-foreground data-focus:bg-blue-500 data-focus:text-white',
                      option.disabled && 'text-foreground-subtle cursor-not-allowed'
                    )}
                  >
                    {/* Option content - support for custom content or text */}
                    <span className="block font-normal group-data-selected:font-semibold">
                      {option.content || option.text}
                    </span>
                    
                    {/* Checkmark for selected option */}
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-500 dark:text-blue-400 group-data-focus:text-white group-not-data-selected:hidden">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  </ListboxOption>
                ))
              )}
            </div>
          </ListboxOptions>
        </div>
      </Listbox>
      
      {error && <p className="text-sm mt-1 text-text-error">{error}</p>}
    </div>
  )
}

export default Dropdown
